import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Badge,
  BUTTON_CLASS,
  DataTable,
  formatUtcDate,
  MetaRow,
  primaryButtonStyle,
  Select,
  StatusDot,
  token,
  type BadgeTone,
  type DataTableColumn,
  type SelectOption,
} from '@bp-files/ui';
import { EnrollmentLock } from '@/components/Users/EnrollmentLock';
import { GrantEditor } from '@/components/Users/GrantEditor';
import { InviteMemberForm } from '@/components/Users/InviteMemberForm';
import { USERS_COPY } from '@/components/Users/users.copy';
import { useSpaces } from '@/lib/useSpaces';
import { useUsers } from '@/lib/useUsers';
import { messageFrom } from '@bp-files/client';
import {
  designateAdmin,
  reactivateMember,
  resetMemberPasskeys,
  revokeAdmin,
  revokeMember,
  setMemberEnrollment,
  type Member,
} from '@/lib/api';

/**
 * The Members tab — the whole of SPEC §9.1 (USR-1 through USR-6).
 *
 * The roster, its filters, the per-row standing actions, the grant editor, and
 * now the invitation that is the only way anybody gets onto the list in the
 * first place.
 *
 * **Rendering a row is not authorization.** SEC-5 puts that in the API, which
 * refuses a non-admin caller on every request whatever this component drew.
 */

/**
 * Status tones, following `SpaceList`'s reasoning. `Invited` is amber because
 * the design system reserves amber for time and for *active* — a handshake in
 * flight is exactly that. `Revoked` is not alert-red: revoking is a normal and
 * reversible administrative act (USR-3), not a fault.
 */
const STATUS_TONE: Record<Member['status'], BadgeTone> = {
  Invited: 'amber',
  Active: 'update',
  Revoked: 'neutral',
};

type StatusFilter = 'all' | Member['status'];
type RoleFilter = 'all' | 'admins' | 'members';

const STATUS_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: USERS_COPY.filters.status.all },
  { value: 'Invited', label: USERS_COPY.filters.status.Invited },
  { value: 'Active', label: USERS_COPY.filters.status.Active },
  { value: 'Revoked', label: USERS_COPY.filters.status.Revoked },
];

const ROLE_FILTER_OPTIONS: SelectOption[] = [
  { value: 'all', label: USERS_COPY.filters.role.all },
  { value: 'admins', label: USERS_COPY.filters.role.admins },
  { value: 'members', label: USERS_COPY.filters.role.members },
];

/**
 * Every column but Actions, which is built by a factory instead — its cell
 * carries a callback, which module scope has no business holding.
 *
 * Link access used to be a column here, between Role and Last active, showing
 * a member's grant chips with the cell itself as the edit affordance. It is a
 * **Space Access** button in the actions column now (product direction), so
 * the table states who someone is and when they were last seen; opening the
 * drawer is the only way to see or change what they can reach.
 */
const staticColumns: DataTableColumn<Member>[] = [
  {
    id: 'member',
    header: USERS_COPY.columns.member,
    sortable: true,
    sortValue: (member: Member) => member.name,
    cell: (member: Member) => (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium" style={{ color: token('bp-text') }}>
          {member.name}
        </span>
        <span className="text-xs" style={{ color: token('bp-text-muted') }}>
          {member.email}
        </span>
      </div>
    ),
  },
  {
    id: 'status',
    header: USERS_COPY.columns.status,
    sortable: true,
    sortValue: (member: Member) => member.status,
    cell: (member: Member) => (
      <Badge tone={STATUS_TONE[member.status]}>{USERS_COPY.status[member.status]}</Badge>
    ),
  },
  {
    id: 'role',
    header: USERS_COPY.columns.role,
    // Rendered only for an admin. A "Member" badge on every other row would be
    // a badge that carries no information, in a system where a badge is a cue.
    cell: (member: Member) =>
      member.isAdmin ? <Badge tone="info">{USERS_COPY.role.admin}</Badge> : null,
  },
  {
    id: 'lastActive',
    header: USERS_COPY.columns.lastActive,
    sortable: true,
    // Undefined sorts **first** in `DataTable`'s ascending comparator — see
    // `compareSortValues`. A member who has never signed in leads this column.
    sortValue: (member: Member) => member.lastActiveAt,
    cell: (member: Member) =>
      member.lastActiveAt === undefined ? (
        <span className="text-xs" style={{ color: token('bp-text-muted') }}>
          {USERS_COPY.lastActive.never}
        </span>
      ) : (
        // MetaRow owns the amber-for-time rule, so using it is how this cell
        // gets the treatment right without each caller remembering.
        <MetaRow
          time={formatUtcDate(member.lastActiveAt, USERS_COPY.dateLocale)}
          dateTime={member.lastActiveAt}
        />
      ),
  },
  {
    /**
     * USR-12's count. **A number, never a list** — what model of authenticator
     * an officer carries is not their agency's business, and USR-11 shows that
     * to the person it belongs to. This answers the only question an admin
     * actually has, which is whether somebody can get in.
     *
     * Zero is rendered plainly rather than as a warning. It used to be the
     * system's ordinary state — an account with no passkey signed in with a
     * code — and USR-14 changed that: a zero now means somebody who can only
     * get in while the Enrollment column beside it is on. Still not a warning,
     * because the pair of cells says it better than a colour would.
     */
    id: 'passkeys',
    header: USERS_COPY.columns.passkeys,
    sortable: true,
    sortValue: (member: Member) => member.passkeyCount,
    cell: (member: Member) => (
      <span className="text-xs" style={{ color: token('bp-text-secondary') }}>
        {String(member.passkeyCount)}
      </span>
    ),
  },
];

/**
 * The Enrollment column (USR-14).
 *
 * **A control, not a reading**, which is what separates it from every other
 * column on this table: the rest state what a record is, and this one is how an
 * admin authorises a credential. It is a factory for `actionsColumn`'s reason —
 * the cell carries a callback and busy state, and module scope has no business
 * holding either.
 *
 * **It moves without anybody touching it.** Enrolling a passkey spends the
 * unlock, so a row an admin turned on this morning reads off again this
 * afternoon with no second click in the log. That is the requirement rather
 * than a surprise, and it is the reason the control is not optimistic: `run`
 * refetches, and the server's answer is routinely ahead of the browser's.
 *
 * **A padlock, not a `<Switch>`.** `EnrollmentLock`'s own header has the
 * argument: a switch means two ordinary positions, and these two are not
 * ordinary in the same way — locked is where every row rests, and unlocked is a
 * window somebody opened.
 *
 * Sortable, because "who can enrol right now" is the question this column is
 * opened for, and `DataTable`'s comparator already takes a boolean.
 */
function enrollmentColumn(options: {
  busyUserId: string | undefined;
  onToggle: (member: Member, unlocked: boolean) => void;
}): DataTableColumn<Member> {
  const { busyUserId, onToggle } = options;

  return {
    id: 'enrollment',
    header: USERS_COPY.columns.enrollment,
    sortable: true,
    sortValue: (member: Member) => member.enrollmentUnlocked,
    /*
      **The cell is one glyph, so the column centres** — and it has to be said
      here rather than on the glyph, because `align` governs the header too and
      a centred column under a left-aligned label reads as an accident.
      `EnrollmentLock` draws both of its branches in the same 36px box so that
      this declaration is what decides where they sit.
    */
    align: 'center',
    cell: (member: Member) => (
      <EnrollmentLock
        unlocked={member.enrollmentUnlocked}
        /*
          **Live on the reader's own row too, unlike everything in the Actions
          column** (ADR-0100). It was read-only here, on the argument that
          unlocking your own enrolment is opening your own window. The window it
          opens is one the admin has already walked through: this route refuses a
          restricted session (SYS-10), so reaching the control at all takes a
          passkey, and what self-unlocking buys is a second device without a
          colleague in the loop. An admin who has lost their only credential has
          no session to press it from and still asks another admin — which is
          where USR-14's second person is load-bearing and here it is not.

          Visibility, not authorization: the API decides (SEC-5, USR-14).
        */
        onChange={(next) => {
          onToggle(member, next);
        }}
        // Named per row: several controls reading "Enrollment" are
        // indistinguishable to a screen reader, and the row context that
        // separates them is visual. `USERS_COPY.actions.more` does the same for
        // the overflow trigger one column over.
        label={
          member.enrollmentUnlocked
            ? USERS_COPY.enrollment.lock(member.name)
            : USERS_COPY.enrollment.unlock(member.name)
        }
        title={
          member.enrollmentUnlocked
            ? USERS_COPY.enrollment.unlockedState(member.name)
            : USERS_COPY.enrollment.lockedState(member.name)
        }
        busy={busyUserId === member.id}
      />
    ),
  };
}

/**
 * The actions column (USR-3, USR-5).
 *
 * Appended by a factory called in the component body rather than declared at
 * module scope, so the busy and confirming state stays out of module scope —
 * the shape `SpaceList` established.
 *
 * A row offers at most two acts, and which two follows the record's state.
 * Revoke, demote, and reset confirm first; reactivate and designate do not,
 * because neither takes anything away. Confirming used to replace this whole
 * cell with an inline pair; it now renders inside `MemberRowActions`' own
 * panel instead, so the cell always renders that one trigger regardless of
 * state.
 */
function actionsColumn(options: {
  busyUserId: string | undefined;
  viewerUserId: string;
  confirming: { userId: string; act: 'revoke' | 'demote' | 'reset' } | undefined;
  onRevoke: (member: Member) => void;
  onReactivate: (member: Member) => void;
  onDesignate: (member: Member) => void;
  onDemote: (member: Member) => void;
  onReset: (member: Member) => void;
  onAsk: (userId: string, act: 'revoke' | 'demote' | 'reset') => void;
  onCancel: () => void;
  onEditGrants: (member: Member) => void;
}): DataTableColumn<Member> {
  const {
    busyUserId,
    viewerUserId,
    confirming,
    onRevoke,
    onReactivate,
    onDesignate,
    onDemote,
    onReset,
    onAsk,
    onCancel,
    onEditGrants,
  } = options;

  return {
    id: 'actions',
    header: USERS_COPY.columns.actions,
    align: 'end',
    cell: (member: Member) => {
      /*
        **No acts on your own row**, which is why this returns before anything
        else is computed.

        Every act in this column is one an admin performs *on somebody else* —
        revoking, demoting, resetting a credential, editing grants. Turned on
        yourself they are all a foot-gun and one of them (demote, on the last
        admin) is the refusal CON-10 already spends a 409 on. Rendering nothing
        is `FolderView`'s own shape for a row with no applicable act, rather than
        a disabled trigger that opens a menu of things that cannot be chosen.

        **Confirming is a prop passed straight through, not a second early
        return.** `MemberRowActions` decides whether to show its item list or
        the confirm/cancel pair from `awaiting`, so this cell always renders
        the same trigger.

        Visibility, not authorization (SEC-5). An admin who wants their own
        access changed asks another admin, which is the same answer CON-10 gives
        an agency with one.
      */
      if (member.id === viewerUserId) return null;

      const busy = busyUserId === member.id;
      const awaiting = confirming?.userId === member.id ? confirming.act : undefined;

      const confirmLabel =
        awaiting === undefined
          ? undefined
          : awaiting === 'revoke'
            ? busy
              ? USERS_COPY.actions.revoking
              : USERS_COPY.actions.confirmRevoke
            : awaiting === 'reset'
              ? busy
                ? USERS_COPY.actions.resetting
                : USERS_COPY.actions.confirmReset
              : busy
                ? USERS_COPY.actions.demoting
                : USERS_COPY.actions.confirmDemote;

      return (
        <MemberRowActions
          member={member}
          busy={busy}
          awaiting={awaiting}
          confirmLabel={confirmLabel}
          cancelLabel={USERS_COPY.actions.cancel}
          onReset={() => {
            onAsk(member.id, 'reset');
          }}
          onEditGrants={() => {
            onEditGrants(member);
          }}
          onDesignate={() => {
            onDesignate(member);
          }}
          onDemote={() => {
            onAsk(member.id, 'demote');
          }}
          onReactivate={() => {
            onReactivate(member);
          }}
          onRevoke={() => {
            onAsk(member.id, 'revoke');
          }}
          onConfirm={() => {
            if (awaiting === 'revoke') onRevoke(member);
            else if (awaiting === 'reset') onReset(member);
            else if (awaiting === 'demote') onDemote(member);
          }}
          onCancel={onCancel}
        />
      );
    },
  };
}

const ROW_ACTIONS_GAP = 4;
// One trigger, up to four items behind it — roomy enough that the estimate
// never has to be exact, on `FolderView`'s `RowActions` precedent for why an
// estimate is fine at all: the menu closes on scroll or resize rather than
// tracking its position live.
const ROW_ACTIONS_HEIGHT = 200;

/**
 * A member's standing acts, behind one trigger (USR-3, USR-5, USR-11a).
 *
 * **`FolderView`'s `RowActions` is the model, not a shared component** — the
 * acts here (reset, space access, designate/demote, reactivate/revoke) share
 * nothing with a file's (download, delete, rehydrate) beyond "a row's
 * standing acts belong behind one control instead of a rank of buttons that
 * outgrew the row". The mechanics are copied on purpose: a portal into
 * `document.body`, positioned from the trigger's own `getBoundingClientRect()`
 * rather than `absolute`, because `DataTable` wraps every table in
 * `overflow-x-auto` — and per the CSS overflow spec, setting `overflow-x` to
 * anything but `visible` forces the used `overflow-y` to `auto` too, whatever
 * the class list says, which clips an `absolute` panel at the scrollport's
 * edge no `z-index` can fix.
 *
 * **Confirmation lives inside the menu.** Selecting Reset, Demote, or Revoke
 * arms `actionsColumn`'s `confirming` state without closing this panel — those
 * three items' `onClick` skips the `setMenuOpen(false)` every other item
 * calls — and the panel then renders the confirm/cancel pair below in place of
 * the item list. Cancel closes immediately; Confirm does not — an effect
 * below closes the panel once `awaiting` clears, which only a successful act
 * does, so a failed one leaves the confirmation up to read and retry, exactly
 * as it always has. This used to be the opposite: the pair rendered as
 * `actionsColumn`'s own inline replacement for this whole cell, outside the
 * menu entirely.
 */
function MemberRowActions({
  member,
  busy,
  awaiting,
  confirmLabel,
  cancelLabel,
  onReset,
  onEditGrants,
  onDesignate,
  onDemote,
  onReactivate,
  onRevoke,
  onConfirm,
  onCancel,
}: {
  member: Member;
  busy: boolean;
  /** Set once one of Reset/Demote/Revoke has been selected, and cleared by `onCancel` or `onConfirm`. */
  awaiting: 'revoke' | 'demote' | 'reset' | undefined;
  /** The confirm item's label, already busy-aware. Undefined exactly when `awaiting` is. */
  confirmLabel: string | undefined;
  cancelLabel: string;
  onReset: () => void;
  onEditGrants: () => void;
  onDesignate: () => void;
  onDemote: () => void;
  onReactivate: () => void;
  onRevoke: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    right: number;
    top?: number;
    bottom?: number;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /**
   * **The race `FolderView`'s `controlsPointerDown` already named.** A click
   * fires mousedown → blur → mouseup → click in that order: the panel's own
   * `onMouseDown` below clears any *existing* `blurTimer`, but blur fires
   * immediately after and arms a *new* one before the click's own handler
   * (Reset, Demote, or Revoke, arming `awaiting` without closing the menu)
   * ever runs — so the leftover timer closed the panel out from under its own
   * confirm step about 120ms later, before this flag existed. Every item here
   * already manages its own close explicitly, so suppressing the trigger's
   * scheduled close for the length of any press in the panel costs nothing.
   */
  const panelPointerDown = useRef(false);
  const previousAwaiting = useRef(awaiting);

  /**
   * Closes the menu when a confirmed act **succeeds** — the one case Confirm's
   * own click cannot know synchronously, since `onConfirm` fires an async
   * request rather than awaiting one. `actionsColumn`'s `run` clears
   * `confirming` (so `awaiting` goes from set to `undefined`) only once that
   * request resolves; a failure leaves it set, and this effect does nothing,
   * which is what keeps the confirmation up for a person to read the error and
   * decide again — the same shape a failed act has always had here.
   */
  useEffect(() => {
    if (previousAwaiting.current !== undefined && awaiting === undefined) {
      setMenuOpen(false);
    }
    previousAwaiting.current = awaiting;
  }, [awaiting]);

  useEffect(() => {
    if (!menuOpen) return;

    // Closes rather than re-tracks, on `RowActions`' own reasoning: a menu
    // whose position goes stale mid-scroll is worse than one that closes a
    // beat sooner than it strictly had to.
    function close() {
      setMenuOpen(false);
    }

    window.addEventListener('scroll', close, { capture: true });
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, { capture: true });
      window.removeEventListener('resize', close);
    };
  }, [menuOpen]);

  const canReset = member.passkeyCount > 0 && member.status !== 'Revoked';
  const canEditGrants = !member.isAdmin;
  const canDesignate = member.status === 'Active' && !member.isAdmin;
  const canDemote = member.status === 'Active' && member.isAdmin;
  const canReactivate = member.status === 'Revoked';

  return (
    <span className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={menuOpen}
        aria-label={USERS_COPY.actions.more(member.name)}
        onClick={() => {
          setMenuOpen((open) => {
            const next = !open;
            if (next) {
              const rect = triggerRef.current?.getBoundingClientRect();
              if (rect) {
                const right = window.innerWidth - rect.right;
                // A row can sit against the visible viewport's lower edge;
                // open into the available space above it instead of
                // clipping the actions below the screen.
                const opensAbove =
                  rect.bottom + ROW_ACTIONS_GAP + ROW_ACTIONS_HEIGHT > window.innerHeight;

                setMenuPosition(
                  opensAbove
                    ? {
                        bottom: Math.max(
                          ROW_ACTIONS_GAP,
                          window.innerHeight - rect.top + ROW_ACTIONS_GAP,
                        ),
                        right,
                      }
                    : { top: rect.bottom + ROW_ACTIONS_GAP, right },
                );
              } else {
                setMenuPosition(null);
              }
            }
            return next;
          });
        }}
        onBlur={() => {
          // Suppressed for the length of a press inside the panel — see
          // `panelPointerDown`'s own header for the race this closes.
          if (panelPointerDown.current) return;

          // Deferred so a click on the panel's own item lands before it
          // unmounts — the same timing `SearchableSelect` and `RowActions`
          // both use.
          blurTimer.current = setTimeout(() => {
            setMenuOpen(false);
          }, 120);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') setMenuOpen(false);
        }}
        className="ml-auto flex items-center justify-center rounded-md p-1.5 text-secondary transition-colors hover:bg-elevated hover:text-body"
      >
        <MoreActionsIcon />
      </button>

      {menuOpen && menuPosition
        ? createPortal(
            <div
              className="fixed z-20 min-w-[11rem] overflow-hidden rounded-md border py-1 shadow-lg"
              style={{
                top: menuPosition.top,
                bottom: menuPosition.bottom,
                right: menuPosition.right,
                borderColor: token('bp-border'),
                background: token('bp-panel'),
                boxShadow: token('shadow-md'),
              }}
              // Beats the trigger's blur timer so the click below commits.
              onMouseDown={() => {
                if (blurTimer.current) clearTimeout(blurTimer.current);
                panelPointerDown.current = true;
              }}
              onMouseUp={() => {
                panelPointerDown.current = false;
              }}
            >
              {awaiting !== undefined ? (
                // CON-26's shape on this surface: selecting Reset, Demote, or
                // Revoke below arms this instead of closing the menu, and
                // lands here in its place.
                <>
                  <RowMenuItem
                    onClick={() => {
                      setMenuOpen(false);
                      onCancel();
                    }}
                  >
                    {cancelLabel}
                  </RowMenuItem>
                  <RowMenuItem
                    danger
                    disabled={busy}
                    onClick={() => {
                      // Does not close the menu itself — the effect above
                      // does, once `awaiting` clears, which only a success
                      // does. A failure leaves this open to read and retry.
                      onConfirm();
                    }}
                  >
                    {confirmLabel}
                  </RowMenuItem>
                </>
              ) : (
                <>
                  {canReset ? (
                    <RowMenuItem
                      onClick={() => {
                        // Arms rather than closes — `onReset` here is
                        // `actionsColumn`'s `onAsk`, not the mutation.
                        onReset();
                      }}
                    >
                      {USERS_COPY.actions.resetPasskeys}
                    </RowMenuItem>
                  ) : null}

                  {canEditGrants ? (
                    <RowMenuItem
                      onClick={() => {
                        setMenuOpen(false);
                        onEditGrants();
                      }}
                    >
                      {USERS_COPY.actions.spaceAccess}
                    </RowMenuItem>
                  ) : null}

                  {canDesignate ? (
                    <RowMenuItem
                      disabled={busy}
                      onClick={() => {
                        setMenuOpen(false);
                        onDesignate();
                      }}
                    >
                      {busy ? USERS_COPY.actions.designating : USERS_COPY.actions.designate}
                    </RowMenuItem>
                  ) : null}

                  {canDemote ? (
                    <RowMenuItem
                      onClick={() => {
                        // Arms rather than closes — see the Reset item above.
                        onDemote();
                      }}
                    >
                      {USERS_COPY.actions.demote}
                    </RowMenuItem>
                  ) : null}

                  {canReactivate ? (
                    <RowMenuItem
                      disabled={busy}
                      onClick={() => {
                        setMenuOpen(false);
                        onReactivate();
                      }}
                    >
                      {busy ? USERS_COPY.actions.reactivating : USERS_COPY.actions.reactivate}
                    </RowMenuItem>
                  ) : (
                    <RowMenuItem
                      danger
                      onClick={() => {
                        // Arms rather than closes — see the Reset item above.
                        onRevoke();
                      }}
                    >
                      {USERS_COPY.actions.revoke}
                    </RowMenuItem>
                  )}
                </>
              )}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

/** One item in `MemberRowActions`' menu — `RowActions`' own item styling. */
function RowMenuItem({
  onClick,
  disabled,
  danger = false,
  children,
}: {
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`block w-full px-3 py-2 text-left text-xs font-medium text-secondary transition-colors hover:bg-elevated ${
        danger ? 'hover:text-danger' : 'hover:text-body'
      }`}
    >
      {children}
    </button>
  );
}

/** Lucide's `ellipsis-vertical`, hand-copied — `FolderView`'s `MoreActionsIcon` sets the precedent (no `lucide-react` dependency). */
function MoreActionsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width={16}
      height={16}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="19" r="1" />
    </svg>
  );
}

export interface MembersTabProps {
  /**
   * `me.agency.domain`, for USR-2's rule. A prop rather than a second
   * `useMe()`: that hook has exactly one call site by design, and a second
   * would create a second session state machine that can disagree with the
   * first. `SpaceList` takes `isAdmin` the same way and for the same reason.
   */
  agencyDomain: string;
  /**
   * `me.user.id` — which row on this roster is the reader's own.
   *
   * Drilled for exactly the reason `agencyDomain` above is, and it is the same
   * shape `SpaceList` uses one surface over: that component takes
   * `viewerAgencyId` to tell removing a member from leaving a space, because
   * both are one route apart only by their target. This is the per-*person*
   * version of the same question, and the answer is that every act in the
   * Actions column is one an admin does to somebody else. The enrolment column
   * used to ask it too and no longer does (ADR-0100), which is why one column
   * reads this prop rather than two.
   */
  viewerUserId: string;
}

export function MembersTab({ agencyDomain, viewerUserId }: MembersTabProps) {
  const { state, retry, refresh } = useUsers();
  // The grant editor picks from the agency's Active spaces, so the surface needs
  // them. A second `GET /spaces` per Users load, which the space list already
  // makes on its own surface.
  const spaces = useSpaces();
  const [status, setStatus] = useState<StatusFilter>('all');
  const [role, setRole] = useState<RoleFilter>('all');
  const [editing, setEditing] = useState<Member | undefined>();
  const [inviting, setInviting] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | undefined>();
  const [confirming, setConfirming] = useState<
    { userId: string; act: 'revoke' | 'demote' | 'reset' } | undefined
  >();
  const [actionError, setActionError] = useState<string | undefined>();

  /**
   * Run a mutation and refetch.
   *
   * The list is refetched rather than patched: the status badge, the admin
   * badge, and — once an admin is designated — the whole grant chip list all
   * move together (PERM-2), so the server's answer is the only one worth
   * rendering.
   */
  async function run(member: Member, action: (id: string) => Promise<unknown>) {
    setBusyUserId(member.id);
    setActionError(undefined);

    try {
      await action(member.id);
      setConfirming(undefined);
      refresh();
    } catch (caught) {
      // Unlike the space list's single generic line, this surfaces the API's own
      // message where there is one. CON-10's refusal names the remedy —
      // designate another admin first — and discarding that would leave the
      // admin stuck on a rule they cannot see.
      setActionError(messageFrom(caught, USERS_COPY.actions.failed));
    } finally {
      setBusyUserId(undefined);
    }
  }

  if (state.kind === 'loading') {
    return (
      <p role="status" className="text-sm" style={{ color: token('bp-text-muted') }}>
        {USERS_COPY.states.loading}
      </p>
    );
  }

  if (state.kind === 'error') {
    // A refusal is permanent — the API decided, and a retry button would
    // promise something it cannot deliver.
    const refused = state.status === 403;

    return (
      <div className="flex flex-col items-start gap-3">
        <p role="alert" className="flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {refused ? USERS_COPY.states.forbidden : USERS_COPY.states.error}
        </p>

        {refused ? null : (
          <button
            type="button"
            onClick={retry}
            className="w-fit border px-3 py-1.5 text-xs font-medium transition-colors"
            style={{
              borderColor: token('bp-border-strong'),
              color: token('bp-text'),
              borderRadius: token('radius-md'),
            }}
          >
            {USERS_COPY.states.retry}
          </button>
        )}
      </div>
    );
  }

  // USR-6, client-side — which is honest only because `GET /users` returns the
  // whole list rather than a page. `FolderView` leaves sorting off for the
  // opposite reason: there the server pages, so a client-side pass would order
  // one page and call it the folder.
  const shown = state.members.filter(
    (member) =>
      (status === 'all' || member.status === status) &&
      (role === 'all' || (role === 'admins') === member.isAdmin),
  );

  // Only Active spaces can be granted: `Requested` has no space yet (CON-6) and
  // `Severed` has lost it (CON-14). The space list shows all three; this does
  // not, because a tick against either would be a promise the API refuses.
  const activeSpaces =
    spaces.state.kind === 'loaded'
      ? spaces.state.spaces.filter((space) => space.status === 'Active')
      : [];

  const shownColumns = [
    ...staticColumns,
    enrollmentColumn({
      busyUserId,
      // Through the same `run` as every standing act, so the toggle shares the
      // busy state, the error surface, and the refetch-not-patch discipline.
      // It matters more here than elsewhere: the server's value moves on its
      // own when somebody enrols, so a patched row would go stale silently.
      onToggle: (member, unlocked) => void run(member, (id) => setMemberEnrollment(id, unlocked)),
    }),
    actionsColumn({
      busyUserId,
      viewerUserId,
      confirming,
      onRevoke: (member) => void run(member, revokeMember),
      onReactivate: (member) => void run(member, reactivateMember),
      onDesignate: (member) => void run(member, designateAdmin),
      onDemote: (member) => void run(member, revokeAdmin),
      // Goes through the same `run` as every other standing act, so it shares
      // the busy label, the error surface, and the refetch. USR-12's point is
      // that a reset is *one control*, and reusing this is what makes it one.
      onReset: (member) => void run(member, (id) => resetMemberPasskeys(id)),
      onEditGrants: (member) => {
        setActionError(undefined);
        setEditing(member);
      },
      onAsk: (userId, act) => {
        setActionError(undefined);
        setConfirming({ userId, act });
      },
      onCancel: () => {
        setConfirming(undefined);
      },
    }),
  ];

  return (
    <div className="flex flex-col gap-4">
      {/*
        The filters and the one act that is not per-row. Invite sits here rather
        than in the table because it creates a row instead of changing one, and
        it carries the blue glow: the design system reserves that for the primary
        act, and on a roster the primary act is adding to it.

        It renders only in the loaded state, with the filters. A 403 means the
        API has already refused this caller everything (SEC-5), and a 500 means
        the surface cannot show what an invitation would add to.
      */}
      <div className="flex flex-wrap items-center gap-4">
        {/*
          `Select`, not `SearchableSelect`: these are three- and four-option
          enums, and a filter you have to type into to find "Active" is worse
          than one you can see all of.
        */}
        <label
          className="flex items-center gap-2 text-xs"
          style={{ color: token('bp-text-muted') }}
        >
          {USERS_COPY.filters.status.label}
          <Select
            label={USERS_COPY.filters.status.label}
            value={status}
            onChange={(next) => {
              setStatus(next as StatusFilter);
            }}
            options={STATUS_FILTER_OPTIONS}
          />
        </label>

        <label
          className="flex items-center gap-2 text-xs"
          style={{ color: token('bp-text-muted') }}
        >
          {USERS_COPY.filters.role.label}
          <Select
            label={USERS_COPY.filters.role.label}
            value={role}
            onChange={(next) => {
              setRole(next as RoleFilter);
            }}
            options={ROLE_FILTER_OPTIONS}
          />
        </label>

        <button
          type="button"
          onClick={() => {
            setActionError(undefined);
            setInviting(true);
          }}
          className={`ml-auto ${BUTTON_CLASS}`}
          style={primaryButtonStyle()}
        >
          {USERS_COPY.invite.open}
        </button>
      </div>

      {actionError === undefined ? null : (
        <p role="alert" className="flex items-center gap-2 text-sm text-danger">
          <StatusDot tone="alert" />
          {actionError}
        </p>
      )}

      <DataTable
        columns={shownColumns}
        rows={shown}
        rowKey={(member) => member.id}
        caption={USERS_COPY.caption}
        emptyState={USERS_COPY.states.empty}
        // Its name column, ascending — the resting order every table in this
        // app opens on. Honest here for the reason the filters above are:
        // `GET /users` does not page. If it ever does, this and they move
        // server-side together.
        defaultSort={{ id: 'member', direction: 'asc' }}
      />

      {/*
        Mounted only while a member is being edited, and **keyed by their id**.
        Both matter: the editor seeds its ticks from `member.grants` on mount,
        so without the key a second member opened while the first's drawer was
        still mounted would inherit their ticks — a grant nobody chose. On save
        it closes and the list refetches; the server owns the chips, and an
        admin designation moves all of them at once (PERM-2).
      */}
      {editing === undefined ? null : (
        <GrantEditor
          key={editing.id}
          open
          member={editing}
          spaces={activeSpaces}
          onClose={() => {
            setEditing(undefined);
          }}
          onSaved={refresh}
        />
      )}

      {/*
        Mounted only while open, so the fields reset between invitations — an
        address left in the box from the last one is the wrong default for the
        next. Same `activeSpaces` the editor gets: only Active spaces can be
        granted (CON-6, CON-14), at invite time as much as afterwards.
      */}
      {inviting ? (
        <InviteMemberForm
          open
          agencyDomain={agencyDomain}
          spaces={activeSpaces}
          onClose={() => {
            setInviting(false);
          }}
          // Refetch, never patch: the server owns name-ascending ordering, and
          // the new row sorts into the middle of the list rather than onto it.
          onInvited={refresh}
        />
      ) : null}
    </div>
  );
}
